import { ComponentProps, useState } from "react";
import { css, useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { ContentCardScaffold } from "../contents/cards/scaffolds/ContentCardScaffold";
import { ArchiveTags } from "./ArchiveTags";
import { ArchiveIconButton } from "lib/icons";
import { Bookmark } from "components/core/inputs/Bookmark";
import { Download } from "components/core/inputs/Download";
import { getGoogleDriveThumbnailUrl } from "utils/googleDrive";
import Image from "next/image";
import { useArchiveBookmark } from "hooks/useArchiveBookmark";
import { useAuth } from "hooks/useAuth";

type ArchiveItem = {
  id: string;
  title: string;
  source: string;
  sourcename: string;
  mix_archive_sourcename?: {
    imgurl?: string | null;
    source: string;
    sourcename: string;
  } | null;
  view_url: string;
  download_url: string;
  thumbnail_url?: string | null;
  tags?: Array<string> | null;
  created_at?: any | null;
  updated_at?: any | null;
  bookmarked?: boolean;
};

const ThumbnailWrapper = styled.div`
  position: relative;
  width: 70px;
  height: 70px;
  flex-shrink: 0;
`;



// Calculate time difference for "X일전" display
const getTimeAgo = (date: any) => {
  if (!date) return "";
  const now = new Date();
  const past = new Date(date);
  const diffInDays = Math.floor((now.getTime() - past.getTime()) / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) return "오늘";
  if (diffInDays === 1) return "1일전";
  return `${diffInDays}일전`;
};

export const ArchiveHorizontalCard = ({
  archive,
  tagEnums,
  onClick,
  onDownload,
  isGuest = false,
  ...props
}: Omit<ComponentProps<typeof ContentCardScaffold>, "children"> & {
  archive: ArchiveItem;
  tagEnums: Array<{ value: string; comment?: string | null }>;
  onClick?: () => void;
  onDownload?: (e: React.MouseEvent) => void;
  isGuest?: boolean;
}) => {
  const theme = useTheme();
  const [isDownloadClicked, setIsDownloadClicked] = useState(false);
  const { toggle: toggleBookmark } = useArchiveBookmark(archive);
  const { openSignInModal } = useAuth();

  // Generate thumbnail from view_url if thumbnail_url is not available
  const thumbnailUrl =
    archive.thumbnail_url ||
    (archive.view_url ? getGoogleDriveThumbnailUrl(archive.view_url, "w1920") : null);

  const [showFallback, setShowFallback] = useState(!thumbnailUrl);

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (isGuest) {
      openSignInModal();
      return;
    }

    // Show orange color momentarily
    setIsDownloadClicked(true);
    setTimeout(() => {
      setIsDownloadClicked(false);
    }, 200);

    if (onDownload) {
      onDownload(e);
    } else {
      window.open(archive.download_url, "_blank");
    }
  };

  const handleBookmark = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleBookmark();
  };

  return (
    <ContentCardScaffold
      {...props}
      onClick={onClick}
    >
      <div
        css={css`
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 8px;
          margin-bottom: 12px;
        `}
      >
        <header
          css={css`
            display: flex;
            align-items: center;
            gap: 8px;
            flex: 1;
            min-width: 0;
          `}
        >
          <Image
            src={archive.mix_archive_sourcename?.imgurl || `https://www.google.com/s2/favicons?domain=${archive.source}&sz=64`}
            alt={archive.sourcename}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
            width={20}
            height={20}
            unoptimized
          />
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 2px;
              flex: 1;
              min-width: 0;
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 4px;
              `}
            >
              <span
                css={css`
                  font-size: 13px;
                  font-weight: 600;
                  color: var(--color-text-primary);
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                `}
              >
                {archive.sourcename}
              </span>
            </div>
            {archive.created_at && (
              <span
                css={css`
                  font-size: 12px;
                  color: var(--color-text-secondary);
                  flex-shrink: 0;
                `}
              >
                {getTimeAgo(archive.created_at)}
              </span>
            )}
          </div>
        </header>
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 8px;

            > div {
              animation: buttonSlideFromRight 0.25s ease-out forwards;
              opacity: 0;

              &:nth-of-type(1) {
                animation-delay: 0.05s;
              }

              &:nth-of-type(2) {
                animation-delay: 0.15s;
              }

              @keyframes buttonSlideFromRight {
                from {
                  opacity: 0;
                  transform: translateX(15px);
                }
                to {
                  opacity: 1;
                  transform: translateX(0);
                }
              }
            }
          `}
        >
          <div
            onClick={handleDownload}
            css={css`
              cursor: pointer;
            `}
          >
            <Download
              variant="filled"
              checked={isDownloadClicked}
              onChange={() => { }}
            />
          </div>
          <div
            onClick={handleBookmark}
            css={css`
              cursor: pointer;
            `}
          >
            <Bookmark
              variant="filled"
              checked={archive.bookmarked || false}
              onChange={() => { }}
            />
          </div>
        </div>
      </div>

      <div
        css={css`
          display: flex;
          gap: 12px;
          margin-bottom: 10px;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 4px;
            flex: 1;
            min-width: 0;
          `}
        >
          <ContentCardScaffold.Title
            css={css`
              font-size: 16px;
              font-weight: 600;
              line-height: 1.4;
              display: -webkit-box;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
              overflow: hidden;
            `}
          >
            {archive.title}
          </ContentCardScaffold.Title>
        </div>
        <ThumbnailWrapper>
          <ContentCardScaffold.ThumbnailImg
            css={css`
              border-radius: 8px;
            `}
            src={thumbnailUrl ?? ""}
            unoptimized
            fill={true}
            onFallbackChange={setShowFallback}
          />
        </ThumbnailWrapper>
      </div>

      <div css={css`
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
      `}>
        {archive.tags && archive.tags.length > 0 ? (
          <ArchiveTags tags={archive.tags} tagEnums={tagEnums} />
        ) : <div />}
        <ArchiveIconButton />
      </div>

    </ContentCardScaffold>
  );
};
